/**
 * LedgerEntryEditorModal — Main compact modal with tabs, field rendering, and validation.
 * Material-UI v4 Dialog + Tabs + TabPanel pattern with design system integration.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  AppBar,
  Tabs,
  Tab,
  Box,
  Typography,
  LinearProgress,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faSave, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { logger } from 'utils/logger';

import { useGetListings } from 'hooks/useGetListings';
import {
  LISTING_CODE_ALLOWED_LEDGER_KINDS,
  LISTING_CODE_ALLOWED_LEDGER_LETTERS,
  LISTING_CODE_ALLOWED_LEDGER_CODES,
} from 'utils/constants';

import { LedgerEditorConfig, deriveVisibleTabs, isSingleFieldMode, EntryFieldKey, FieldKey } from './config';
import FieldRenderer from './fields/FieldRenderer';
import ListFieldRenderer from './fields/ListFieldRenderer';
import ConfirmDialog from './ConfirmDialog';
import SubmitStateRenderer from './SubmitStateRenderer';
import { useLedgerEditorEngine, LedgerEditorEngine } from './engine/useLedgerEditorEngine';
import { EntryDraft } from './engine/dirty';
import { getFieldType, normalizeListingToOptions } from './utils/format';
import { getAccountingTranslation } from './utils/translation';
import { sanitizeDescriptionRemovePayee } from './utils/sanitize';

function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function deriveEntryFieldRowGroups(entryFields?: EntryFieldKey[]): EntryFieldKey[][] {
  if (!entryFields || entryFields.length === 0) {
    return [];
  }

  const fieldsSet = new Set(entryFields);

  const rows: EntryFieldKey[][] = [
    ['code', 'kind'],
    ['description'],
    ['letter', 'number'],
    ['amount'],
    ['entryDate'],
    ['months', 'months_advanced'],
  ];

  return rows
    .map(row => row.filter(field => fieldsSet.has(field)) as EntryFieldKey[])
    .filter(row => row.length > 0);
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 400,
      maxWidth: 600,
      [theme.breakpoints.down('md')]: {
        minWidth: 'auto',
        width: '90vw',
      },
    },
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(2),
  },
  appBar: {
    marginBottom: theme.spacing(2),
  },
  tab: {
    minWidth: 90,
    textTransform: 'none',
  },
  tabContainer: {
    minHeight: 400,
    [theme.breakpoints.down('md')]: {
      minHeight: 300,
    },
  },
  tabContent: {
    padding: theme.spacing(2),
  },
  entriesRowContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      alignItems: 'stretch',
    },
  },
  entriesFieldWrapper: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  amountPostedRowContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      alignItems: 'stretch',
    },
  },
  amountWrapper: {
    flex: 1,
    minWidth: 0,
  },
  postedCheckboxWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing(1),
  },
  inlineFieldsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  inlineFieldWrapper: {
    flex: 1,
  },
  inlineCheckboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  actions: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  submitButton: {
    minWidth: 100,
  },
  saveButtonIcon: {
    fontSize: '16px',
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  saveButtonText: {
    textTransform: 'none',
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  cancelButtonText: {
    textTransform: 'none',
  },
  cancelButtonIcon: {
    fontSize: '16px',
    marginRight: theme.spacing(1),
  },
  deleteButton: {
    marginRight: 'auto',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  deleteButtonIcon: {
    fontSize: '16px',
    marginRight: theme.spacing(1),
    color: theme.palette.error.contrastText,
  },
}));

function TabPanel({ children, value, index, id }) {
  const tabClasses = useStyles();
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={id}
      style={{ display: value !== index ? 'none' : 'block' }}
    >
      {value === index && <Box className={tabClasses.tabContent}>{children}</Box>}
    </div>
  );
}

export interface LedgerEntryEditorModalProps {
	open: boolean;
	initialEntry: EntryDraft;
	config: LedgerEditorConfig;
	title?: string;
	onClose: () => void;
	onSubmit?: (engine: LedgerEditorEngine) => Promise<void>;
	enableDelete?: boolean;
}

/**
 * Compact modal for editing ledger entries with field-level configuration.
 * Supports single-field and multi-tab presentation with design system styling.
 */
export const LedgerEntryEditorModal: React.FC<LedgerEntryEditorModalProps> = ({
  open,
  initialEntry,
  config,
  title,
  onClose,
  onSubmit,
  enableDelete = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Fetch listing data for kind, letter, and code selectors
  // Using React Query for caching; only fetches if fields are present in config
  const hasKindField = useMemo(() =>
		config.entryFields?.includes('kind') as boolean,
  [config.entryFields]
  );
  const hasLetterField = useMemo(() =>
		config.entryFields?.includes('letter') as boolean,
  [config.entryFields]
  );
  const hasCodeField = useMemo(() =>
		config.entryFields?.includes('code') as boolean,
  [config.entryFields]
  );

  const kindsListing = useGetListings({
    code: LISTING_CODE_ALLOWED_LEDGER_KINDS,
    queryConfig: { enabled: hasKindField },
  });

  const lettersListing = useGetListings({
    code: LISTING_CODE_ALLOWED_LEDGER_LETTERS,
    queryConfig: { enabled: hasLetterField },
  });

  const codesListing = useGetListings({
    code: LISTING_CODE_ALLOWED_LEDGER_CODES,
    queryConfig: { enabled: hasCodeField },
  });

  // Normalize listings to FieldRenderer options format
  const kindOptions = useMemo(
    () => normalizeListingToOptions(kindsListing.data?.getListing.entries),
    [kindsListing.data]
  );

  const letterOptions = useMemo(
    () => normalizeListingToOptions(lettersListing.data?.getListing.entries),
    [lettersListing.data]
  );

  const codeOptions = useMemo(
    () => normalizeListingToOptions(codesListing.data?.getListing.entries),
    [codesListing.data]
  );

  // Create entry with sanitized description for consistent form/title rendering
  const sanitizedEntry = useMemo(() => {
    const sanitizedDesc = sanitizeDescriptionRemovePayee(initialEntry.description, initialEntry.involved_parties);
    return sanitizedDesc !== initialEntry.description
      ? { ...initialEntry, description: sanitizedDesc }
      : initialEntry;
  }, [initialEntry]);

  // Setup form with sanitized entry
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: sanitizedEntry,
  });

  // Memoize capabilities to prevent infinite loop
  const capabilities = useMemo(() => ({
    canEditEntry: (config.entryFields?.length || 0) > 0,
    canEditAssociation: (config.associationFields?.length || 0) > 0,
    canEditParties: (config.partyFields?.length || 0) > 0,
    canEditLinkedEntry: (config.linkFields?.length || 0) > 0,
    canTogglePosted: config.enablePosted,
    canDeleteEntry: config.enableDelete,
  }), [config.entryFields, config.associationFields, config.partyFields, config.linkFields, config.enablePosted, config.enableDelete]);

  // Memoize engine config
  const engineConfig = useMemo(() => ({
    onClose,
    capabilities,
  }), [onClose, capabilities]);

  // Setup engine with sanitized entry
  const engine = useLedgerEditorEngine(formMethods, sanitizedEntry, engineConfig);

  // Derive visible tabs
  const visibleTabs = useMemo(() => deriveVisibleTabs(config), [config]);
  const isSingleField = useMemo(() => isSingleFieldMode(config), [config]);

  // Clamp selected tab index when visible tabs change
  if (visibleTabs.length > 0 && selectedTabIndex >= visibleTabs.length) {
    setSelectedTabIndex(visibleTabs.length - 1);
  }

  // Handle submit
  const handleSubmit = async () => {
    try {
      if (onSubmit) {
        await onSubmit(engine);
      } else {
        await engine.submit();
      }
    } catch (error) {
      logger.error('Submit error:', error);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmDialogOpen(false);
    await engine.deleteEntry();
  };

  // Title precedence: sanitized description → explicit title → localized fallback
  const displayTitle = sanitizedEntry.description || title || getAccountingTranslation('edit-ledger-entry', 'Edit Ledger Entry');

  return (
    <>
      <Dialog
        open={open}
        onClose={engine.isSubmitting ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        className={classes.dialog}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6" component="div">
            {displayTitle}
          </Typography>
        </DialogTitle>

        {/* Tabs only if multiple tabs */}
        {!isSingleField && visibleTabs.length > 1 && (
          <AppBar position="relative" className={classes.appBar} color="inherit" elevation={0}>
            <Tabs
              value={selectedTabIndex}
              onChange={(event, newValue) => setSelectedTabIndex(newValue)}
              aria-label="ledger entry tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {visibleTabs.map((tab, index) => (
                <Tab key={tab.id} label={getAccountingTranslation(`tabs:${tab.label}`, capitalize(tab.label))} className={classes.tab} />
              ))}
            </Tabs>
          </AppBar>
        )}

        <DialogContent>
          <SubmitStateRenderer
            isSubmitting={engine.isSubmitting}
            errors={engine.errors}
            fieldErrors={engine.fieldErrors}
            queueError={engine.queueError}
            hasPartialSuccess={
							engine.queue.items?.some((item) => item.result !== undefined) || false
						}
            onRetry={() => handleSubmit()}
          />

          <Box className={classes.tabContainer}>
            {visibleTabs.map((tab, index) => {
						  const renderedFields = new Set<string>();

						  const renderField = (fieldKey: FieldKey) => {
						    if (renderedFields.has(fieldKey as string)) {
						      return null;
						    }

						    if ((fieldKey as any) === 'involved_parties' || tab.id === 'party') {
						      return (
  <ListFieldRenderer
    key={fieldKey}
    fieldKey={fieldKey}
    fieldName={fieldKey}
    control={formMethods.control}
    label={getAccountingTranslation(`fields:${fieldKey}`, 'Parties')}
    itemFields={[
											  { key: 'name', label: getAccountingTranslation('fields:partyName', 'Name'), type: 'text' },
											  { key: 'kind', label: getAccountingTranslation('fields:partyKind', 'Kind'), type: 'text' },
											  { key: 'amount', label: getAccountingTranslation('fields:amount', 'Amount'), type: 'currency' },
											  { key: 'percent', label: getAccountingTranslation('fields:percent', 'Percent'), type: 'percent' },
    ]}
    disabled={engine.isSubmitting}
    error={engine.fieldErrors[fieldKey]}
  />
						      );
						    }

						    if (fieldKey === 'amount' && config.enablePosted) {
						      renderedFields.add('posted');
						      return (
  <Box key={fieldKey} className={classes.amountPostedRowContainer}>
    <Box className={classes.amountWrapper}>
      <FieldRenderer
        fieldKey={fieldKey}
        fieldName={fieldKey}
        control={formMethods.control}
        label={getAccountingTranslation(`fields:${fieldKey}`, capitalize(String(fieldKey)))}
        type={getFieldType(String(fieldKey))}
        disabled={engine.isSubmitting}
        error={engine.fieldErrors[fieldKey]}
      />
    </Box>
    <Box className={classes.postedCheckboxWrapper}>
      <FieldRenderer
        fieldKey="posted"
        fieldName="posted"
        control={formMethods.control}
        label={getAccountingTranslation('fields:posted', 'Posted')}
        type="checkbox"
        disabled={engine.isSubmitting}
        error={engine.fieldErrors.posted}
      />
    </Box>
  </Box>
						      );
						    }

						    if (fieldKey === 'posted') {
						      return null;
						    }

						    const fieldKeyStr = String(fieldKey);
						    let fieldOptions;
						    if (fieldKeyStr === 'kind') {
						      fieldOptions = kindOptions;
						    } else if (fieldKeyStr === 'letter') {
						      fieldOptions = letterOptions;
						    } else if (fieldKeyStr === 'code') {
						      fieldOptions = codeOptions;
						    }

						    return (
  <FieldRenderer
    key={fieldKey}
    fieldKey={fieldKey}
    fieldName={fieldKey}
    control={formMethods.control}
    label={getAccountingTranslation(`fields:${fieldKey}`, capitalize(fieldKeyStr))}
    type={getFieldType(fieldKeyStr)}
    options={fieldOptions}
    required={config.requireDescription && fieldKey === 'description'}
    disabled={engine.isSubmitting}
    error={engine.fieldErrors[fieldKey]}
  />
						    );
						  };

						  return (
  <TabPanel key={tab.id} value={selectedTabIndex} index={index} id={`tab-panel-${tab.id}`}>
    {tab.id === 'entry' ? (
									  deriveEntryFieldRowGroups(tab.fields as EntryFieldKey[]).map((row) => (
  <Box key={row.join('-')} className={classes.entriesRowContainer}>
    {row.map(fieldKey => (
      <Box key={fieldKey} className={classes.entriesFieldWrapper}>
        {renderField(fieldKey)}
      </Box>
    ))}
  </Box>
									  ))
    ) : (
									  tab.fields.map((fieldKey) => {
									    if (renderedFields.has(fieldKey as string)) {
									      return null;
									    }
									    return renderField(fieldKey);
									  })
    )}
  </TabPanel>
						  );
            })}
          </Box>
        </DialogContent>

        <DialogActions className={classes.actions}>
          {enableDelete && (
          <Button
            onClick={handleDelete}
            disabled={engine.isSubmitting}
            className={classes.deleteButton}
          >
            <FontAwesomeIcon icon={faTrashAlt} className={classes.deleteButtonIcon} />
            <Typography variant="body2" style={{ textTransform: 'none' }}>
              {getAccountingTranslation('common:delete', 'Delete')}
            </Typography>
          </Button>
          )}

          <Button
            onClick={onClose}
            disabled={engine.isSubmitting}
            variant="outlined"
          >
            <FontAwesomeIcon icon={faTimes} className={classes.cancelButtonIcon} />
            <Typography variant="body2" className={classes.cancelButtonText}>
              {getAccountingTranslation('common:cancel', 'Cancel')}
            </Typography>
          </Button>

          <Button
            onClick={handleSubmit}
            color="secondary"
            variant="contained"
            disableElevation
            disabled={!engine.canSubmit || engine.isSubmitting}
            className={classes.submitButton}
          >
            {!engine.isSubmitting && <FontAwesomeIcon icon={faSave} className={classes.saveButtonIcon} />}

            {engine.isSubmitting && <FontAwesomeIcon spin icon={faSpinner} />}

            {!engine.isSubmitting && (
            <Typography variant="body2" className={classes.saveButtonText}>
              {getAccountingTranslation('common:save', 'Save')}
            </Typography>
            )}

            {engine.isSubmitting && (
            <Typography variant="body2" className={classes.saveButtonText}>
              {getAccountingTranslation('common:saving', 'Saving...')}
            </Typography>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialogOpen}
        title={getAccountingTranslation('confirmDialog:deleteTitle', 'Confirm Delete')}
        message={getAccountingTranslation('confirmDialog:deleteMessage', 'This action cannot be undone.')}
        confirmText={getAccountingTranslation('common:delete', 'Delete')}
        cancelText={getAccountingTranslation('common:cancel', 'Cancel')}
        loading={engine.isSubmitting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialogOpen(false)}
      />
    </>
  );
};

export default LedgerEntryEditorModal;
